/**
 * React Query hooks backing the admin-web alerts screens (014-alerts / US7).
 *
 * Key conventions match the rest of admin-web:
 *   - Flat query keys rooted at ['admin-alerts', ...]
 *   - Mutations invalidate the overview key on success so the grid catches up
 *     without waiting for its 10s interval tick.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/adminAlertsApi';
import type {
  AdminAlertHistoryFilter,
  StartSimulationRequest,
} from '../api/adminAlertsApi';

const adminAlertsKeys = {
  all: ['admin-alerts'] as const,
  overview: () => [...adminAlertsKeys.all, 'overview'] as const,
  history: (filter: AdminAlertHistoryFilter) =>
    [...adminAlertsKeys.all, 'history', filter] as const,
  regions: (type: string | undefined) => [...adminAlertsKeys.all, 'regions', type ?? 'all'] as const,
};

/** Polls the cross-tenant overview every 10s per the Session 2026-04-18 Q4 freshness budget. */
export function useAlertsOverview() {
  return useQuery({
    queryKey: adminAlertsKeys.overview(),
    queryFn: () => api.getAlertsOverview(),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

export function useAlertsHistory(filter: AdminAlertHistoryFilter) {
  return useQuery({
    queryKey: adminAlertsKeys.history(filter),
    queryFn: () => api.getAlertsHistory(filter),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Start-simulation mutation. Throws on 409 (concurrent sim for the region)
 * and 422 (invalid duration) — caller surfaces the server message in a toast.
 */
export function useStartSimulation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: StartSimulationRequest) => api.startSimulation(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminAlertsKeys.overview() });
    },
  });
}

export function useEndSimulation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertEventId: string) => api.endSimulation(alertEventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminAlertsKeys.overview() });
    },
  });
}

/**
 * Ukraine region list for admin-web pickers. Backed by the local mirror that
 * `UkraineRegionSyncWorker` refreshes every 24h, so the data is effectively
 * static across a single admin session — set a long stale time to keep the
 * request out of the hot path on every render.
 *
 * Defaults to returning the full hierarchy (State → District → Community),
 * which is what the RegionPicker needs for nested selection. Callers that
 * specifically only need oblasts can pass `type="State"`.
 */
export function useAdminRegions(type?: string) {
  return useQuery({
    queryKey: adminAlertsKeys.regions(type),
    queryFn: () => api.listRegions(type),
    staleTime: 60 * 60 * 1_000, // 1h
    gcTime: 24 * 60 * 60 * 1_000, // keep in cache for a day
  });
}
